import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PROD_SITE_KEY,
  PRE_PROD_SITE_KEY,
  QUERY_STRING,
  RECAPTCHA_URL,
  WIDGET_ID,
  RecaptchaLocale,
  convertToRecaptchaLocale,
  WIDGET_TEST_ID,
  RECAPTCHA_TITLE
} from './ReCaptcha.util';
import { isCloverProdEnvironment, isCypressEnvironment } from 'util/Environment';
import { useScript } from '@clover-util/script-registry';
import { createHookContext } from '@clover-util/react-context';

export type CaptchaExecuteFunction = () => Promise<Maybe<RecaptchaToken>>;

const SITE_KEY = isCloverProdEnvironment() ? PROD_SITE_KEY : PRE_PROD_SITE_KEY;

export const { ReCaptchaProvider, useReCaptcha } = createHookContext('ReCaptcha', () => {
  const { i18n } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const [captchaInjected, setCaptchaInjected] = useState(false);
  const captchaRef = useRef<Element>();
  const widgetRef = useRef<HTMLElement>();
  const overlayRef = useRef<HTMLElement>();

  const [scriptIsLoaded] = useScript(RECAPTCHA_URL + QUERY_STRING);

  const recaptchaLocale = convertToRecaptchaLocale(i18n.language as RecaptchaLocale);

  const resolveRef = useRef<(token: RecaptchaToken) => void>();
  const rejectRef = useRef<any>();

  const attemptReCaptcha: CaptchaExecuteFunction = useCallback(async () => {
    if (isCypressEnvironment()) return Promise.resolve(undefined);

    if (!isReady) throw new Error('ReCaptcha is not ready');

    const tokenPromise = new Promise<RecaptchaToken>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });

    // eslint-disable-next-line functional/immutable-data
    if (widgetRef.current) widgetRef.current.style.display = '';
    // eslint-disable-next-line functional/immutable-data
    if (overlayRef.current) overlayRef.current.style.display = '';

    window.grecaptcha?.execute();

    return tokenPromise;
  }, [isReady]);

  useEffect(() => {
    if (!scriptIsLoaded) return; // no-op if script isn't ready

    const widgetNode = document.createElement('widgetNode');
    /* eslint-disable functional/immutable-data */
    widgetNode.id = WIDGET_ID;
    widgetNode.style.position = 'fixed';
    widgetNode.style.top = '40vh';
    widgetNode.style.left = '0';
    widgetNode.style.display = 'none';
    widgetNode.dataset.testid = WIDGET_TEST_ID;
    /* eslint-enable functional/immutable-data */
    document.body.appendChild(widgetNode);
    widgetRef.current = widgetNode;

    window.grecaptcha?.ready(() => {

      window.grecaptcha?.render(widgetNode, {
        'sitekey': SITE_KEY,
        'size': 'invisible',
        'callback': token => {
          resolveRef.current?.(token);
          window.grecaptcha?.reset(); // reset captcha in case we need to retrigger
        },
        'error-callback': () => rejectRef.current?.('captchaError'),
        'expired-callback': async () => {
          window.grecaptcha?.reset(); // reset captcha if expired token
          rejectRef.current?.('captchaError');
        },
        'hl': recaptchaLocale,
      });

      setIsReady(true);
    });

    return () => widgetNode.remove();
  }, [recaptchaLocale, scriptIsLoaded]);

  const handleRecaptchaChallenge = useCallback((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type !== 'childList') continue;

      for (const node of mutation.addedNodes) {
        const reCaptchaIframe = (node as Element).querySelector(`iframe[title^="${RECAPTCHA_TITLE}"]`);
        const reCaptchaOverlay = reCaptchaIframe ? reCaptchaIframe.parentElement : null;

        if (!reCaptchaOverlay) continue;

        /* eslint-disable functional/immutable-data */
        reCaptchaOverlay.style.position = 'fixed';
        reCaptchaOverlay.style.top = '5vh';
        reCaptchaOverlay.style.display = 'none';
        /* eslint-enable functional/immutable-data*/

        captchaRef.current = reCaptchaOverlay.parentElement || undefined;
        overlayRef.current = reCaptchaOverlay;
        setCaptchaInjected(true);
      }
    }
  }, []);

  const handleRecaptchaChanges = useCallback((mutationsList: MutationRecord[], observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type !== 'attributes') continue;

      if ((mutation.target as HTMLElement).style.opacity === '0') {
        rejectRef.current?.('reCaptcha Closed');
      }
    }
  },[]);

  useEffect(() => {
    let observer: Maybe<MutationObserver>;

    if (isReady && window.MutationObserver) {
      observer = new window.MutationObserver(handleRecaptchaChallenge);

      observer.observe(document.body, { childList: true });
    }

    return () => observer?.disconnect();
  }, [handleRecaptchaChallenge, isReady]);

  useEffect(() => {
    let observer: Maybe<MutationObserver>;

    if (captchaInjected && captchaRef.current && window.MutationObserver) {
      observer = new window.MutationObserver(handleRecaptchaChanges);

      observer.observe(captchaRef.current, { attributes: true });
    }

    return () => observer?.disconnect();
  }, [handleRecaptchaChanges, captchaInjected]);

  return {
    isReady: isReady || isCypressEnvironment(),
    locale: recaptchaLocale,
    attemptReCaptcha
  };
});
